import React, { useEffect, useRef } from 'react';

import { Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';

import { modalOptions } from 'Helpers/modalOptions';

import CreatableSelectField from 'CommonComponents/CreatableSelectField';
import Modal from 'CommonComponents/Modal';
import ModalFooter from 'CommonComponents/ModalFooter';
import ModalHeader from 'CommonComponents/ModalHeader';
import SelectFromField from 'CommonComponents/SelectFromField';
import TextFormField from 'CommonComponents/TextFormField';

import useModal from 'Hooks/useModal';
import { useToast } from 'Hooks/useToast';

const RoleSchema = Yup.object().shape({
    name: Yup.string().required($t('Role name is required')),
    category: Yup.string().required($t('Category is required')),
    // Free text, deliberately optional: an organisation that prices everything
    // per day has nothing useful to type here, and an expense line simply
    // inherits no unit.
    unit: Yup.string().max(50, $t('Unit must be 50 characters or fewer')),
    cost_rate: Yup.number().typeError($t('Enter a number')).min(0, $t('Cost rate cannot be negative')).required($t('Cost rate is required')),
    // A billed rate of zero has no margin to compute — the server refuses it too.
    billed_rate: Yup.number().typeError($t('Enter a number')).moreThan(0, $t('Billed rate must be more than zero')).required($t('Billed rate is required')),
    // A margin of 100% or more would need an infinite billed rate. With no cost
    // rate every billed rate is a 100% margin, so the bound only applies to a cost.
    margin: Yup.number()
        .typeError('Enter a number')
        .when('cost_rate', {
            is: (cost) => cost > 0,
            then: (schema) => schema.lessThan(100, 'Margin must be less than 100%'),
        }),
    // At -100% or below the billed rate would be zero or negative.
    markup: Yup.number().typeError('Enter a number').moreThan(-100, 'Mark-up must be more than -100%'),
    default_type: Yup.string().oneOf(['internal', 'freelance']).required(),
    status: Yup.string().oneOf(['active', 'archived']).required(),
});

/** Rounded for an input, as a string, so a blank figure stays blank. */
function rounded(value, places) {
    return Number.isFinite(value) ? String(Math.round(value * 10 ** places) / 10 ** places) : '';
}

/**
 * Margin and mark-up for a pair of rates, for display only — the server works
 * out the figures the rate card shows and a budget line books. Either is blank
 * where its divisor is missing.
 */
function percentagesFor(cost, billed) {
    return {
        margin: billed > 0 ? rounded((1 - cost / billed) * 100, 1) : '',
        markup: cost > 0 ? rounded(((billed - cost) / cost) * 100, 1) : '',
    };
}

/**
 * Billed rate, margin and mark-up kept in step, with the cost rate as the
 * anchor. Editing a percentage sets the billed rate and the other percentage;
 * editing either rate keeps the billed rate and recalculates both percentages.
 * Nothing changes until a field is edited, so saving a role untouched posts
 * its billed rate exactly as stored. Without a cost rate there is nothing to
 * mark up or anchor to, so the percentages are shown but not editable.
 */
function PricingFields() {
    const { values, setValues, setFieldTouched } = useFormikContext();

    const cost = parseFloat(values.cost_rate);
    const hasCost = cost > 0;

    const onRateChange = (e) => {
        const next = { ...values, [e.target.name]: e.target.value };

        setValues({ ...next, ...percentagesFor(parseFloat(next.cost_rate), parseFloat(next.billed_rate)) });
    };

    const onMarginChange = (e) => {
        const margin = parseFloat(e.target.value);
        const next = { ...values, margin: e.target.value };

        if (Number.isFinite(margin) && margin < 100) {
            next.markup = rounded((margin / (100 - margin)) * 100, 1);
            next.billed_rate = rounded(cost / (1 - margin / 100), 2);
        }

        // Touched now, so an out-of-range figure is flagged while it is typed
        // rather than silently left out of the billed rate.
        setFieldTouched('margin', true, false);
        setValues(next);
    };

    const onMarkupChange = (e) => {
        const markup = parseFloat(e.target.value);
        const next = { ...values, markup: e.target.value };

        if (Number.isFinite(markup) && markup > -100) {
            next.margin = rounded((markup / (100 + markup)) * 100, 1);
            next.billed_rate = rounded(cost * (1 + markup / 100), 2);
        }

        setFieldTouched('markup', true, false);
        setValues(next);
    };

    return (
        <>
            <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2">
                <TextFormField label={$t('Cost rate')} name="cost_rate" type="number" min="0" step="0.01" required onChange={onRateChange} />

                <SelectFromField label={$t('Default type')} as="select" name="default_type" required>
                    <option value="internal">{$t('Internal')}</option>
                    <option value="freelance">{$t('Freelance')}</option>
                </SelectFromField>
            </div>

            <div className="tw-flex tw-flex-col tw-gap-4 tw-rounded-lg tw-bg-blue-10 tw-p-4">
                <p className="tw-text-sm tw-text-charcoal-75 tw-mb-0">Enter either percentage; the other and the billed rate update automatically.</p>

                <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2">
                    <TextFormField label="Margin %" name="margin" type="number" step="0.1" disabled={!hasCost} onChange={onMarginChange} />
                    <TextFormField label="Mark-up %" name="markup" type="number" step="0.1" disabled={!hasCost} onChange={onMarkupChange} />
                </div>

                <TextFormField label={$t('Billed rate')} name="billed_rate" type="number" min="0" step="0.01" required onChange={onRateChange} />
            </div>
        </>
    );
}

/**
 * Free text with a suggestion list drawn from the categories already on this
 * version, so near-duplicates stop fragmenting the grouped table without
 * turning categories into a managed list of their own.
 */
function CategoryField({ suggestions }) {
    const { values, errors, touched, setFieldValue, setFieldTouched } = useFormikContext();

    const options = Array.from(new Set([...suggestions, values.category].filter(Boolean))).map((category) => ({
        value: category,
        label: category,
    }));

    return (
        <div className="tw-flex tw-flex-col tw-space-y-1.5">
            <label htmlFor="category" className="text-input__label">
                {$t('Category')} *
            </label>
            <CreatableSelectField
                options={options}
                value={values.category}
                onChange={(option) => {
                    setFieldValue('category', option ? option.value : '');
                    setFieldTouched('category', true, false);
                }}
            />
            {touched.category && errors.category ? <div className="tw-text-red-500 tw-text-sm">{errors.category}</div> : null}
        </div>
    );
}

export default function RateCardRoleModal({ role, categories, storeUrl, updateUrl, onSaved, onClose }) {
    const modal = useModal({ options: { ...modalOptions(false), onHide: onClose }, visible: true });
    const toast = useToast();

    // Flowbite parks its backdrop outside the React tree, so unmounting while the
    // modal is still shown would leave the overlay behind.
    const modalStateRef = useRef(modal);
    modalStateRef.current = modal;

    useEffect(
        () => () => {
            const { element } = modalStateRef.current;

            if (element && element.isVisible()) {
                element.hide();
            }
        },
        [],
    );

    const close = () => modal.hide();

    const initialValues = {
        name: role?.name ?? '',
        category: role?.category ?? '',
        unit: role?.unit ?? '',
        cost_rate: role?.cost_rate ?? '',
        billed_rate: role?.billed_rate ?? '',
        ...percentagesFor(parseFloat(role?.cost_rate), parseFloat(role?.billed_rate)),
        default_type: role?.default_type ?? 'internal',
        status: role?.status ?? 'active',
    };

    return (
        <Modal id="rate-card-role-modal" modal={modal}>
            <Formik
                initialValues={initialValues}
                validationSchema={RoleSchema}
                onSubmit={({ margin, markup, ...values }, actions) => {
                    // The percentages are derived from the two rates, which are all that is saved.
                    const request = role ? axios.put(updateUrl(role.id), values) : axios.post(storeUrl, values);

                    return request
                        .then(() => {
                            // Hide first: onSaved refetches the page, which
                            // re-renders the table hosting this modal.
                            close();
                            onSaved();
                        })
                        .catch((e) => {
                            if (e.response?.data?.errors) {
                                actions.setErrors(e.response.data.errors);
                            } else {
                                toast.error($t('Something went wrong'));
                            }
                        })
                        .finally(() => {
                            actions.setSubmitting(false);
                        });
                }}
            >
                {({ isSubmitting, handleSubmit }) => (
                    <>
                        <ModalHeader title={role ? $t('Edit role') : $t('Add role')} close={close} />

                        <div className="modal__form-container">
                            <TextFormField label={$t('Role')} name="name" type="text" required />

                            <CategoryField suggestions={categories} />

                            <TextFormField label={$t('Unit')} name="unit" type="text" maxLength={50} placeholder={$t('e.g. day, half day, each, week')} />

                            <PricingFields />

                            <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2">
                                <SelectFromField label={$t('Status')} as="select" name="status" required>
                                    <option value="active">{$t('Active')}</option>
                                    <option value="archived">{$t('Archived')}</option>
                                </SelectFromField>
                            </div>

                            <ModalFooter
                                cancelButtonText={$t('Cancel')}
                                primaryButtonText={$t('Save')}
                                submitButtonVariant="primary"
                                onCancel={close}
                                handleSubmit={handleSubmit}
                                isSubmitting={isSubmitting}
                            />
                        </div>
                    </>
                )}
            </Formik>
        </Modal>
    );
}
